/**
 * Cause-accurate i18n key when `visiblePaymentMethods.length === 0`.
 * Never labels a non-card failure as `payment.cardUnavailable`.
 */
export type EmptyPaymentMethodsMessageKey =
  | 'payment.cardUnavailable'
  | 'payment.qrUnavailable'
  | 'payment.methodsUnavailable';

export function resolveEmptyPaymentMethodsMessageKey(input: {
  readonly gatewayMethodsPayable: boolean | null;
  readonly mobileQrAllowed: boolean;
  /**
   * When true, `gatewayMethodsPayable === false` means the card/gateway probe failed.
   * When false, payable may still be `false` because the rail was never a candidate —
   * that must not map to card-unavailable.
   */
  readonly gatewayRailCandidate?: boolean;
  /**
   * When true, `!mobileQrAllowed` means QR was a policy candidate but not offerable.
   * When false/omitted with `gatewayRailCandidate === false` and QR not offerable,
   * use neutral `methodsUnavailable` (not qrUnavailable).
   */
  readonly qrRailCandidate?: boolean;
}): EmptyPaymentMethodsMessageKey {
  const {
    gatewayMethodsPayable,
    mobileQrAllowed,
    gatewayRailCandidate,
    qrRailCandidate,
  } = input;

  if (gatewayRailCandidate === true && gatewayMethodsPayable === false) {
    return 'payment.cardUnavailable';
  }

  // QR was a policy candidate but not offerable (true QR-only expected failure).
  if (qrRailCandidate === true && !mobileQrAllowed) {
    return 'payment.qrUnavailable';
  }

  // Neither rail/candidate offerable — including omitted qrRailCandidate with both-false
  // (product always passes explicit gatewayRailCandidate; must not mislabel as QR-unavailable).
  if (
    gatewayRailCandidate === false &&
    (qrRailCandidate === false || (!mobileQrAllowed && qrRailCandidate !== true))
  ) {
    return 'payment.methodsUnavailable';
  }

  // Probe did not fail (or never ran as failure); QR side unavailable.
  if (!mobileQrAllowed && gatewayMethodsPayable !== false) {
    return 'payment.qrUnavailable';
  }

  // Neither rail offerable, or ambiguous non-card empty — neutral copy.
  return 'payment.methodsUnavailable';
}
